import React, { useState, useEffect } from 'react';
import oracleCertBadge from './assets/oracleCert.png';
import medicoLogin from './assets/medico/login.png';
import medicoMedication from './assets/medico/medication.png';
import medicoPrescription from './assets/medico/prescription.png';
import financerDashboard from './assets/financer/dashboard.png';
import financerInvestments from './assets/financer/investments.png';
import financerChitManagement from './assets/financer/chitManagement.png';

export default function App() {
  // Function to close mobile navbar
  const closeNavbar = () => {
    const navbarToggler = document.querySelector('.navbar-toggler');
    const navbarCollapse = document.querySelector('.navbar-collapse');

    if (navbarCollapse && navbarCollapse.classList.contains('show')) {
      navbarToggler.click();
    }
  };

  // Function to scroll to section with offset
  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    closeNavbar();

    const section = document.getElementById(sectionId);
    if (section) {
      // Add a small delay to ensure navbar collapse animation completes
      setTimeout(() => {
        const navbarHeight = document.querySelector('.navbar').offsetHeight;
        const sectionPosition = section.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: sectionPosition - navbarHeight,
          behavior: 'smooth'
        });
      }, 150);
    }
  };
  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    return savedTheme || (prefersDark ? 'dark' : 'light');
  });

  // Messages page state
  const [showMessages, setShowMessages] = useState(false);
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [isDeletingMessage, setIsDeletingMessage] = useState(false);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Contact form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'Job Opportunity',
    message: ''
  });

  const [formErrors, setFormErrors] = useState({
    email: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState({
    success: false,
    message: ''
  });

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset form errors
    setFormErrors({ email: '' });

    // Validate email
    if (!validateEmail(formData.email)) {
      setFormErrors({ email: 'Please enter a valid email address' });
      return;
    }

    setIsSubmitting(true);

    try {
      // Get current browser time and timezone
      const clientTime = new Date();
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const response = await fetch('/api/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: `Subject: ${formData.subject}\n\n${formData.message}`,
          clientTime: clientTime,
          timeZone: timeZone
        })
      });

      const data = await response.json();

      if (response.ok) {
        setFormStatus({
          success: true,
          message: 'Message sent successfully! I will get back to you soon.'
        });
        // Reset form
        setFormData({
          name: '',
          email: '',
          subject: 'Job Opportunity',
          message: ''
        });
      } else {
        setFormStatus({
          success: false,
          message: data.error || 'Failed to send message. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setFormStatus({
        success: false,
        message: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle password authentication and fetch messages
  const handleAuthentication = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`/api/messages?password=${encodeURIComponent(password)}`);
      const data = await response.json();

      if (response.ok) {
        setIsAuthenticated(true);
        setMessages(data.messages);
      } else {
        setError(data.error || 'Authentication failed. Please try again.');
      }
    } catch (error) {
      console.error('Error authenticating:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle messages page visibility
  const toggleMessagesPage = () => {
    setShowMessages(!showMessages);
    if (!showMessages) {
      // Reset state when opening messages page
      setIsAuthenticated(false);
      setPassword('');
      setMessages([]);
      setError('');
      setMessageToDelete(null);
    }
  };

  // Confirm message deletion (shows confirmation UI)
  const confirmDeleteMessage = (message) => {
    setMessageToDelete(message);
  };

  // Cancel message deletion
  const cancelDeleteMessage = () => {
    setMessageToDelete(null);
  };

  // Handle message deletion
  const handleDeleteMessage = async () => {
    if (!messageToDelete || !messageToDelete._id) return;

    setIsDeletingMessage(true);
    setError('');

    try {
      const response = await fetch(`/api/messages/${messageToDelete._id}?password=${encodeURIComponent(password)}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (response.ok) {
        // Remove the deleted message from the state
        setMessages(messages.filter(msg => msg._id !== messageToDelete._id));
        setMessageToDelete(null);
      } else {
        setError(data.error || 'Failed to delete message. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsDeletingMessage(false);
    }
  };

  return (
      <div className="min-vh-100 d-flex flex-column">
        {/* Navigation */}
        <nav className="navbar navbar-expand-lg navbar-dark sticky-top">
          <div className="container">
            <a className="navbar-brand fw-bold d-flex align-items-center" href="#">
              <i className="bi bi-terminal me-2"></i>
              <span className="d-none d-sm-inline">Santhosh Matla</span>
              <span className="d-inline d-sm-none">SM</span>
            </a>
            <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav">
              <span className="navbar-toggler-icon"></span>
            </button>
            <div className="collapse navbar-collapse" id="navbarNav">
              <ul className="navbar-nav ms-lg-auto">
                <li className="nav-item">
                  <a className="nav-link" href="#about" onClick={(e) => scrollToSection(e, 'about')}>
                    <i className="bi bi-person me-1"></i>
                    About
                  </a>
                </li>
                <li className="nav-item">
                  <a className="nav-link" href="#education" onClick={(e) => scrollToSection(e, 'education')}>
                    <i className="bi bi-mortarboard me-1"></i>
                    Education
                  </a>
                </li>
                <li className="nav-item">
                  <a className="nav-link" href="#skills" onClick={(e) => scrollToSection(e, 'skills')}>
                    <i className="bi bi-tools me-1"></i>
                    Skills
                  </a>
                </li>
                <li className="nav-item">
                  <a className="nav-link" href="#projects" onClick={(e) => scrollToSection(e, 'projects')}>
                    <i className="bi bi-code-square me-1"></i>
                    Projects
                  </a>
                </li>
                <li className="nav-item">
                  <a className="nav-link" href="#contact" onClick={(e) => scrollToSection(e, 'contact')}>
                    <i className="bi bi-chat-dots me-1"></i>
                    Contact
                  </a>
                </li>
                <li className="nav-item">
                  <button
                      className="nav-link btn btn-link"
                      onClick={(e) => {
                        closeNavbar();
                        toggleMessagesPage();
                      }}
                  >
                    <i className="bi bi-envelope-open me-1"></i>
                    Messages
                  </button>
                </li>
                <li className="nav-item">
                  <button
                      className="btn btn-outline-light btn-sm w-100 mt-2 mt-lg-0 ms-lg-2"
                      onClick={(e) => {
                        closeNavbar();
                        toggleTheme();
                      }}
                      aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
                  >
                    <i className={`bi ${theme === 'light' ? 'bi-moon' : 'bi-sun'} me-1`}></i>
                    {theme === 'light' ? 'Dark' : 'Light'}
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <header className="hero">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-4 text-center mb-4 mb-lg-0">
                <div className="position-relative d-inline-block">
                  <img
                      src="https://ui-avatars.com/api/?name=Santhosh+Matla&background=4f46e5&color=fff&size=200&font-size=0.35&bold=true"
                      alt="Santhosh Matla"
                      className="rounded-circle shadow-lg"
                      width={200}
                      height={200}
                      style={{ border: '4px solid rgba(255, 255, 255, 0.2)' }}
                  />
                  <div className="position-absolute bottom-0 end-0 bg-success rounded-circle p-2 border border-2 border-white">
                    <i className="bi bi-check-lg text-white"></i>
                  </div>
                </div>
              </div>
              <div className="col-lg-8 text-center text-lg-start">
                <h1 className="display-4 fw-bold mb-3">Santhosh Matla</h1>
                <p className="lead fs-3 mb-4">Backend & Cloud Engineer</p>
                <p className="mb-4 fs-5 opacity-90">
                  Building scalable backend systems and cloud-native infrastructure on AWS.
                  Specializing in backend development, infrastructure as code (Terraform), and cloud automation.
                </p>
                <div className="d-flex gap-3 justify-content-center justify-content-lg-start">
                  <a href="https://ca.linkedin.com/in/santhoshmatla" className="btn btn-light" target="_blank">
                    <i className="bi bi-linkedin me-2"></i>LinkedIn
                  </a>
                  <a href="mailto:santhosh.matla05@gmail.com" className="btn btn-outline-light">
                    <i className="bi bi-envelope me-2"></i>Email
                  </a>
                  <a href="https://bitbucket.org/santhoshmatla/" className="btn btn-outline-light" target="_blank">
                    <i className="bi bi-code-slash me-2"></i>Bitbucket
                  </a>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="container py-5">
          {/* About Section */}
          <section id="about" className="mb-5 fade-in">
            <div className="card shadow-sm">
              <div className="card-body p-4 p-md-5">
                <div className="d-flex align-items-center mb-4">
                  <i className="bi bi-person-circle fs-3 text-primary me-3"></i>
                  <h2 className="h4 mb-0 fw-bold">About Me</h2>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <p className="mb-4">
                      I'm a passionate software engineer with a strong focus on backend systems, infrastructure as code, and scalable web applications.
                      I specialize in building robust APIs, designing cloud-native architectures on AWS, and modernizing legacy platforms.
                    </p>
                  </div>
                  <div className="col-md-6">
                    <p className="mb-4">
                      I enjoy solving complex engineering problems, automating deployments, and crafting clean, maintainable code.
                      My mission is to build reliable systems that deliver both performance and a great user experience.
                    </p>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="row g-4">
                    <div className="col-sm-6 col-md-3">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-award-fill text-primary fs-4 me-3"></i>
                        <div>
                          <h3 className="h6 mb-0">5+ Years</h3>
                          <small className="text-muted">Experience</small>
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-md-3">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-stack text-primary fs-4 me-3"></i>
                        <div>
                          <h3 className="h6 mb-0">10+ Projects</h3>
                          <small className="text-muted">Completed</small>
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-md-3">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-code-square text-primary fs-4 me-3"></i>
                        <div>
                          <h3 className="h6 mb-0">Open Source</h3>
                          <small className="text-muted">Contributor</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Projects Section */}
          <section id="projects" className="mb-5 fade-in">
            <div className="card shadow-sm">
              <div className="card-body p-4 p-md-5">
                <div className="d-flex align-items-center mb-4">
                  <i className="bi bi-kanban fs-3 text-primary me-3"></i>
                  <h2 className="h4 mb-0 fw-bold">Featured Projects</h2>
                </div>

                <div className="row g-4">

                  {/* Financer */}
                  <div className="col-md-6 col-lg-4">
                    <div className="card h-100 shadow-sm border-0">
                      <div className="card-img-top bg-success bg-opacity-10 p-4 text-center">
                        <i className="bi bi-cash-coin text-success" style={{ fontSize: '3rem' }}></i>
                      </div>
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h3 className="h5 card-title mb-0">
                            Financer <small className="text-muted ms-1" style={{ fontSize: '0.7rem' }}>2025</small>
                          </h3>
                          <span className="badge bg-success text-light">Featured</span>
                        </div>
                        <p className="card-text text-muted">
                          Personal finance management with RRSP, stocks, mutual funds tracking.
                        </p>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          <span className="badge bg-light text-dark border">Node.js</span>
                          <span className="badge bg-light text-dark border">Express</span>
                          <span className="badge bg-light text-dark border">MongoDB</span>
                          <span className="badge bg-light text-dark border">React</span>
                        </div>
                      </div>
                      <div className="card-footer bg-transparent border-top-0 d-flex justify-content-between">
                        <a
                            href="https://www.financer.gamersan.ca"
                            className="btn btn-sm btn-outline-success"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                          <i className="bi bi-eye me-1"></i>Live Demo
                        </a>
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            data-bs-toggle="modal"
                            data-bs-target="#financerScreenshots"
                        >
                          <i className="bi bi-images me-1"></i>Screenshots
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Alcura */}
                  <div className="col-md-6 col-lg-4">
                    <div className="card h-100 shadow-sm border-0">
                      <div className="card-img-top bg-info bg-opacity-10 p-4 text-center">
                        <i className="bi bi-hospital text-info" style={{ fontSize: '3rem' }}></i>
                      </div>
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h3 className="h5 card-title mb-0">
                            Alcura <small className="text-muted ms-1" style={{ fontSize: '0.7rem' }}>2024</small>
                          </h3>
                          <span className="badge bg-warning text-dark">In Progress</span>
                        </div>
                        <p className="card-text text-muted">
                          Healthcare management system with AI-driven insights and analytics.
                        </p>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          <span className="badge bg-light text-dark border">Java</span>
                          <span className="badge bg-light text-dark border">Spring Boot</span>
                          <span className="badge bg-light text-dark border">React</span>
                          <span className="badge bg-light text-dark border">AI/ML</span>
                        </div>
                      </div>
                      <div className="card-footer bg-transparent border-top-0 d-flex justify-content-between">
                        <a
                            href="http://www.alcura.gamersan.ca"
                            className="btn btn-sm btn-outline-info"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                          <i className="bi bi-eye me-1"></i>Preview
                        </a>
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            data-bs-toggle="modal"
                            data-bs-target="#alcuraScreenshots"
                        >
                          <i className="bi bi-images me-1"></i>Screenshots
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* Medico */}
                  <div className="col-md-6 col-lg-4">
                    <div className="card h-100 shadow-sm border-0">
                      <div className="card-img-top bg-danger bg-opacity-10 p-4 text-center">
                        <i className="bi bi-capsule text-danger" style={{ fontSize: '3rem' }}></i>
                      </div>
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h3 className="h5 card-title mb-0">
                            Medico <small className="text-muted ms-1" style={{ fontSize: '0.7rem' }}>2025</small>
                          </h3>
                          <span className="badge bg-success text-light">Completed</span>
                        </div>
                        <p className="card-text text-muted">
                          Medication management application with prescription tracking and medication reminders.
                        </p>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          <span className="badge bg-light text-dark border">Node.js</span>
                          <span className="badge bg-light text-dark border">Express</span>
                          <span className="badge bg-light text-dark border">MongoDB</span>
                          <span className="badge bg-light text-dark border">React</span>
                          <span className="badge bg-light text-dark border">Vite</span>
                        </div>
                      </div>
                      <div className="card-footer bg-transparent border-top-0 d-flex justify-content-between">
                        <a
                            href="https://www.medico.gamersan.ca"
                            className="btn btn-sm btn-outline-danger"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                          <i className="bi bi-eye me-1"></i>Live Demo
                        </a>
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            data-bs-toggle="modal"
                            data-bs-target="#medicoScreenshots"
                        >
                          <i className="bi bi-images me-1"></i>Screenshots
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* TBDBank */}
                  <div className="col-md-6 col-lg-4">
                    <div className="card h-100 shadow-sm border-0">
                      <div className="card-img-top bg-primary bg-opacity-10 p-4 text-center">
                        <i className="bi bi-bank text-primary" style={{ fontSize: '3rem' }}></i>
                      </div>
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h3 className="h5 card-title mb-0">
                            TBDBank <small className="text-muted ms-1" style={{ fontSize: '0.7rem' }}>2023</small>
                          </h3>
                          <span className="badge bg-primary text-light">Featured</span>
                        </div>
                        <p className="card-text text-muted">
                          Digital banking system with account opening, QR payments, and user-friendly interface.
                        </p>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          <span className="badge bg-light text-dark border">Java</span>
                          <span className="badge bg-light text-dark border">Spring Boot</span>
                          <span className="badge bg-light text-dark border">MongoDB</span>
                          <span className="badge bg-light text-dark border">React</span>
                        </div>
                      </div>
                      <div className="card-footer bg-transparent border-top-0 d-flex justify-content-between">
                        <a
                            href="https://www.tbdbank.gamersan.ca"
                            className="btn btn-sm btn-outline-primary"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                          <i className="bi bi-eye me-1"></i>Live Demo
                        </a>
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            data-bs-toggle="modal"
                            data-bs-target="#tbdbankScreenshots"
                        >
                          <i className="bi bi-images me-1"></i>Screenshots
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Education Section */}
          <section id="education" className="mb-5 fade-in">
            <div className="card shadow-sm">
              <div className="card-body p-4 p-md-5">
                <div className="d-flex align-items-center mb-4">
                  <i className="bi bi-mortarboard-fill fs-3 text-primary me-3"></i>
                  <h2 className="h4 mb-0 fw-bold">Education</h2>
                </div>

                <div className="row g-4">
                  {/* Master's Degree */}
                  <div className="col-md-6 col-lg-4">
                    <div className="card h-100 shadow-sm border-0">
                      <div className="card-body">
                        <div className="d-flex align-items-center mb-3">
                          <i className="bi bi-mortarboard text-primary fs-4 me-3"></i>
                          <h3 className="h5 mb-0">Master of Computer Science</h3>
                        </div>
                        <p className="mb-2"><strong>University of Waterloo</strong></p>
                        <p className="mb-2">Waterloo, Ontario, Canada</p>
                      </div>
                    </div>
                  </div>

                  {/* Post Graduation */}
                  <div className="col-md-6 col-lg-4">
                    <div className="card h-100 shadow-sm border-0">
                      <div className="card-body">
                        <div className="d-flex align-items-center mb-3">
                          <i className="bi bi-mortarboard text-success fs-4 me-3"></i>
                          <h3 className="h5 mb-0">Post Graduation in Information Technology Solutions</h3>
                        </div>
                        <p className="mb-2"><strong>Humber College</strong></p>
                        <p className="mb-2">Toronto, Ontario, Canada</p>
                      </div>
                    </div>
                  </div>

                  {/* Bachelor's Degree */}
                  <div className="col-md-6 col-lg-4">
                    <div className="card h-100 shadow-sm border-0">
                      <div className="card-body">
                        <div className="d-flex align-items-center mb-3">
                          <i className="bi bi-mortarboard text-info fs-4 me-3"></i>
                          <h3 className="h5 mb-0">Bachelor of Technology in Computer Science & Engineering</h3>
                        </div>
                        <p className="mb-2"><strong>Kakatiya University</strong></p>
                        <p className="mb-2">Warangal, Telangana, India</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Certifications */}
                <div className="mt-5">
                  <div className="d-flex align-items-center mb-4">
                    <i className="bi bi-award fs-3 text-primary me-3"></i>
                    <h2 className="h4 mb-0 fw-bold">Certifications</h2>
                  </div>

                  <div className="row g-4">
                    <div className="col-md-6">
                      <div className="card h-100 shadow-sm border-0">
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-4 text-center mb-3 mb-md-0">
                              <img 
                                src={oracleCertBadge} 
                                alt="Oracle Certification Badge" 
                                className="img-fluid" 
                                style={{ maxWidth: '120px' }}
                              />
                            </div>
                            <div className="col-md-8">
                              <div className="d-flex align-items-center mb-3">
                                <i className="bi bi-patch-check text-warning fs-4 me-3"></i>
                                <h3 className="h5 mb-0">Oracle Certified Professional Java SE 11 Developer</h3>
                              </div>
                              <p className="mb-2"><strong>Oracle</strong></p>
                              <div className="d-flex justify-content-between align-items-center">
                                <a href="https://www.credly.com/badges/b164dfb1-d175-4f10-b561-de554492b867" 
                                   target="_blank" 
                                   rel="noopener noreferrer" 
                                   className="btn btn-sm btn-outline-danger">
                                  <i className="bi bi-patch-check-fill me-1"></i>Verify
                                </a>
                                <span className="badge bg-warning text-dark">2021</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Skills Section */}
          <section id="skills" className="mb-5 fade-in">
            <div className="card shadow-sm">
              <div className="card-body p-4 p-md-5">
                <div className="d-flex align-items-center mb-4">
                  <i className="bi bi-tools fs-3 text-primary me-3"></i>
                  <h2 className="h4 mb-0 fw-bold">Technical Skills</h2>
                </div>

                <div className="row g-4">
                  {/* Backend Development */}
                  <div className="col-md-6">
                    <div className="skill-category h-100">
                      <h3 className="h5 mb-3">
                        <i className="bi bi-server text-success me-2"></i>
                        Backend Development
                      </h3>
                      <div className="d-flex flex-wrap gap-2">
                        <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25">Node.js</span>
                        <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25">Express</span>
                        <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25">Java</span>
                        <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25">Spring Boot</span>
                        <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25">Python</span>
                        <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25">REST APIs</span>
                        <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25">GraphQL</span>
                      </div>
                    </div>
                  </div>

                  {/* Cloud & DevOps */}
                  <div className="col-md-6">
                    <div className="skill-category h-100">
                      <h3 className="h5 mb-3">
                        <i className="bi bi-cloud text-info me-2"></i>
                        Cloud & DevOps
                      </h3>
                      <div className="d-flex flex-wrap gap-2">
                        <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25">AWS</span>
                        <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25">Terraform</span>
                        <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25">Docker</span>
                        <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25">Kubernetes</span>
                        <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25">CI/CD</span>
                        <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25">Serverless</span>
                      </div>
                    </div>
                  </div>

                  {/* Databases */}
                  <div className="col-md-6">
                    <div className="skill-category h-100">
                      <h3 className="h5 mb-3">
                        <i className="bi bi-database text-warning me-2"></i>
                        Databases
                      </h3>
                      <div className="d-flex flex-wrap gap-2">
                        <span className="badge bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25">MongoDB</span>
                        <span className="badge bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25">PostgreSQL</span>
                        <span className="badge bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25">MySQL</span>
                        <span className="badge bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25">Redis</span>
                        <span className="badge bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25">DynamoDB</span>
                      </div>
                    </div>
                  </div>

                  {/* Frontend */}
                  <div className="col-md-6">
                    <div className="skill-category h-100">
                      <h3 className="h5 mb-3">
                        <i className="bi bi-layout-text-sidebar text-primary me-2"></i>
                        Frontend
                      </h3>
                      <div className="d-flex flex-wrap gap-2">
                        <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25">React</span>
                        <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25">TypeScript</span>
                        <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25">JavaScript</span>
                        <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25">HTML/CSS</span>
                        <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25">Bootstrap</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>



          {/* Contact Section */}
          <section id="contact" className="mb-5 fade-in">
            <div className="card shadow-sm">
              <div className="card-body p-4 p-md-5">
                <div className="d-flex align-items-center mb-4">
                  <i className="bi bi-envelope fs-3 text-primary me-3"></i>
                  <h2 className="h4 mb-0 fw-bold">Get In Touch</h2>
                </div>

                <div className="row g-4">
                  <div className="col-lg-5">
                    <div className="h-100">
                      <h3 className="h5 mb-4">Contact Information</h3>
                      <ul className="list-unstyled">
                        <li className="mb-3">
                          <a href="mailto:santhosh.matla05@gmail.com" className="text-decoration-none d-flex align-items-center">
                            <i className="bi bi-envelope-fill fs-5 text-primary me-3"></i>
                            <span>santhosh.matla05@gmail.com</span>
                          </a>
                        </li>
                        <li className="mb-3">
                          <a href="https://ca.linkedin.com/in/santhoshmatla" className="text-decoration-none d-flex align-items-center" target="_blank">
                            <i className="bi bi-linkedin fs-5 text-primary me-3"></i>
                            <span>linkedin.com/in/santhoshmatla</span>
                          </a>
                        </li>
                        <li className="mb-3">
                          <a href="https://bitbucket.org/santhoshmatla/" className="text-decoration-none d-flex align-items-center" target="_blank">
                            <i className="bi bi-code-square fs-5 text-primary me-3"></i>
                            <span>bitbucket.org/santhoshmatla</span>
                          </a>
                        </li>
                      </ul>

                    </div>
                  </div>

                  <div className="col-lg-7">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body p-4">
                        <h3 className="h5 mb-4">Send Me a Message</h3>
                        {formStatus.message && (
                          <div className={`alert alert-${formStatus.success ? 'success' : 'danger'} mb-3`}>
                            {formStatus.message}
                          </div>
                        )}
                        <form onSubmit={handleSubmit}>
                          <div className="mb-3">
                            <label htmlFor="name" className="form-label">Name</label>
                            <input 
                              type="text" 
                              className="form-control" 
                              id="name" 
                              placeholder="Your Name" 
                              value={formData.name}
                              onChange={(e) => setFormData({...formData, name: e.target.value})}
                              required
                            />
                          </div>
                          <div className="mb-3">
                            <label htmlFor="email" className="form-label">Email</label>
                            <input 
                              type="email" 
                              className={`form-control ${formErrors.email ? 'is-invalid' : ''}`}
                              id="email" 
                              placeholder="your.email@example.com" 
                              value={formData.email}
                              onChange={(e) => {
                                const newEmail = e.target.value;
                                setFormData({...formData, email: newEmail});

                                // Clear error when user starts typing again
                                if (formErrors.email) {
                                  setFormErrors({...formErrors, email: ''});
                                }

                                // Validate email as user types, but only if there's content
                                if (newEmail && !validateEmail(newEmail)) {
                                  setFormErrors({...formErrors, email: 'Please enter a valid email address'});
                                }
                              }}
                              required
                            />
                            {formErrors.email && (
                              <div className="invalid-feedback">
                                {formErrors.email}
                              </div>
                            )}
                          </div>
                          <div className="mb-3">
                            <label htmlFor="subject" className="form-label">Subject</label>
                            <select 
                              className="form-select" 
                              id="subject"
                              value={formData.subject}
                              onChange={(e) => setFormData({...formData, subject: e.target.value})}
                            >
                              <option>Job Opportunity</option>
                              <option>Freelance Project</option>
                              <option>Collaboration</option>
                              <option>Other</option>
                            </select>
                          </div>
                          <div className="mb-3">
                            <label htmlFor="message" className="form-label">Message</label>
                            <textarea 
                              className="form-control" 
                              id="message" 
                              rows="4" 
                              placeholder="Your message here..." 
                              value={formData.message}
                              onChange={(e) => setFormData({...formData, message: e.target.value})}
                              required
                            ></textarea>
                          </div>
                          <button 
                            type="submit" 
                            className="btn btn-primary w-100"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Sending...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-send me-2"></i>Send Message
                              </>
                            )}
                          </button>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="py-5 mt-auto">
          <div className="container">
            <div className="row g-4">
              <div className="col-lg-4">
                <h5 className="text-white mb-3">
                  <i className="bi bi-code-square me-2"></i>
                  Santhosh Matla
                </h5>
                <p className="text-white-50 small">
                  Backend & Cloud Engineer specializing in scalable systems and cloud-native solutions.
                </p>
              </div>
              <div className="col-lg-4">
                <h5 className="text-white mb-3">Quick Links</h5>
                <ul className="list-unstyled">
                  <li className="mb-2"><a href="#about" className="text-white-50 text-decoration-none" onClick={(e) => scrollToSection(e, 'about')}>About</a></li>
                  <li className="mb-2"><a href="#education" className="text-white-50 text-decoration-none" onClick={(e) => scrollToSection(e, 'education')}>Education</a></li>
                  <li className="mb-2"><a href="#skills" className="text-white-50 text-decoration-none" onClick={(e) => scrollToSection(e, 'skills')}>Skills</a></li>
                  <li className="mb-2"><a href="#projects" className="text-white-50 text-decoration-none" onClick={(e) => scrollToSection(e, 'projects')}>Projects</a></li>
                  <li><a href="#contact" className="text-white-50 text-decoration-none" onClick={(e) => scrollToSection(e, 'contact')}>Contact</a></li>
                </ul>
              </div>
              <div className="col-lg-4">
                <h5 className="text-white mb-3">Connect</h5>
                <div className="d-flex gap-3 mb-3">
                  <a href="https://ca.linkedin.com/in/santhoshmatla" className="text-white fs-5" target="_blank">
                    <i className="bi bi-linkedin"></i>
                  </a>
                  <a href="mailto:santhosh.matla05@gmail.com" className="text-white fs-5">
                    <i className="bi bi-envelope"></i>
                  </a>
                  <a href="https://bitbucket.org/santhoshmatla/" className="text-white fs-5" target="_blank">
                    <i className="bi bi-code-square"></i>
                  </a>
                </div>
                <p className="text-white-50 small mb-0">
                  &copy; {new Date().getFullYear()} Santhosh Matla. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        </footer>

        {/* Messages Modal */}
        {showMessages && (
          <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-envelope-open me-2"></i>
                    Messages
                  </h5>
                  <button type="button" className="btn-close" onClick={toggleMessagesPage}></button>
                </div>
                <div className="modal-body">
                  {!isAuthenticated ? (
                    <div>
                      <p className="mb-3">Please enter the password to view messages:</p>
                      {error && <div className="alert alert-danger">{error}</div>}
                      <form onSubmit={handleAuthentication}>
                        <div className="mb-3">
                          <input
                            type="password"
                            className="form-control"
                            placeholder="Enter password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                          />
                        </div>
                        <button 
                          type="submit" 
                          className="btn btn-primary"
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Authenticating...
                            </>
                          ) : (
                            'View Messages'
                          )}
                        </button>
                      </form>
                    </div>
                  ) : (
                    <div>
                      <div className="table-responsive">
                        <table className="table table-striped">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Message</th>
                              <th>Date & Time</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {messages.length > 0 ? (
                              messages.map((msg, index) => (
                                <tr key={index}>
                                  <td>{msg.name}</td>
                                  <td>
                                    <a href={`mailto:${msg.email}`}>{msg.email}</a>
                                  </td>
                                  <td style={{ whiteSpace: 'pre-wrap' }}>{msg.message}</td>
                                  <td>
                                    <div>
                                      <strong>Server:</strong> {new Date(msg.createdAt).toLocaleString()}
                                    </div>
                                    {msg.clientTime && msg.timeZone && (
                                      <div className="small mt-1">
                                        <strong>Client:</strong> {new Date(msg.clientTime).toLocaleString(undefined, { timeZone: msg.timeZone })}
                                        <div className="small text-muted mt-1">
                                          <i className="bi bi-clock me-1"></i>
                                          {msg.timeZone}
                                        </div>
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    <button 
                                      className="btn btn-sm btn-danger"
                                      onClick={() => confirmDeleteMessage(msg)}
                                      title="Delete message"
                                    >
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="5" className="text-center">No messages found</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Delete Confirmation Dialog */}
                      {messageToDelete && (
                        <div className="mt-4">
                          <div className="alert alert-danger">
                            <h5 className="alert-heading">
                              <i className="bi bi-exclamation-triangle-fill me-2"></i>
                              Confirm Deletion
                            </h5>
                            <p>Are you sure you want to delete this message from <strong>{messageToDelete.name}</strong>?</p>
                            <p className="mb-0">This action cannot be undone.</p>

                            <hr />

                            <div className="d-flex justify-content-end gap-2">
                              <button 
                                className="btn btn-secondary" 
                                onClick={cancelDeleteMessage}
                                disabled={isDeletingMessage}
                              >
                                Cancel
                              </button>
                              <button 
                                className="btn btn-danger" 
                                onClick={handleDeleteMessage}
                                disabled={isDeletingMessage}
                              >
                                {isDeletingMessage ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Deleting...
                                  </>
                                ) : (
                                  <>
                                    <i className="bi bi-trash me-2"></i>
                                    Delete
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={toggleMessagesPage}>Close</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Backdrop */}
        {showMessages && <div className="modal-backdrop fade show"></div>}

        {/* Scroll to Top Button */}
        {showScrollTop && (
            <button
                className="btn btn-primary rounded-circle position-fixed bottom-0 end-0 m-4 shadow"
                onClick={scrollToTop}
                style={{ width: '50px', height: '50px' }}
            >
              <i className="bi bi-arrow-up"></i>
            </button>
        )}

        {/* Medico Screenshots Modal */}
        <div className="modal fade" id="medicoScreenshots" tabIndex="-1" aria-labelledby="medicoScreenshotsLabel" aria-hidden="true">
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="medicoScreenshotsLabel">Medico Screenshots</h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div className="modal-body p-0">
                <div id="medicoCarousel" className="carousel slide" data-bs-ride="carousel">
                  <div className="carousel-indicators">
                    <button type="button" data-bs-target="#medicoCarousel" data-bs-slide-to="0" className="active" aria-current="true" aria-label="Login"></button>
                    <button type="button" data-bs-target="#medicoCarousel" data-bs-slide-to="1" aria-label="Medication"></button>
                    <button type="button" data-bs-target="#medicoCarousel" data-bs-slide-to="2" aria-label="Prescription"></button>
                  </div>
                  <div className="carousel-inner">
                    <div className="carousel-item active">
                      <img src={medicoLogin} className="d-block w-100" alt="Medico Login Screen" />
                      <div className="carousel-caption d-none d-md-block bg-dark bg-opacity-50 rounded">
                        <h5>Login Screen</h5>
                        <p>Secure authentication for the Medico application</p>
                      </div>
                    </div>
                    <div className="carousel-item">
                      <img src={medicoMedication} className="d-block w-100" alt="Medication Management" />
                      <div className="carousel-caption d-none d-md-block bg-dark bg-opacity-50 rounded">
                        <h5>Medication Management</h5>
                        <p>Track and manage medications with ease</p>
                      </div>
                    </div>
                    <div className="carousel-item">
                      <img src={medicoPrescription} className="d-block w-100" alt="Prescription Tracking" />
                      <div className="carousel-caption d-none d-md-block bg-dark bg-opacity-50 rounded">
                        <h5>Prescription Tracking</h5>
                        <p>Keep track of prescriptions and refill dates</p>
                      </div>
                    </div>
                  </div>
                  <button className="carousel-control-prev" type="button" data-bs-target="#medicoCarousel" data-bs-slide="prev">
                    <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                    <span className="visually-hidden">Previous</span>
                  </button>
                  <button className="carousel-control-next" type="button" data-bs-target="#medicoCarousel" data-bs-slide="next">
                    <span className="carousel-control-next-icon" aria-hidden="true"></span>
                    <span className="visually-hidden">Next</span>
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
              </div>
            </div>
          </div>
        </div>

        {/* Financer Screenshots Modal */}
        <div className="modal fade" id="financerScreenshots" tabIndex="-1" aria-labelledby="financerScreenshotsLabel" aria-hidden="true">
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="financerScreenshotsLabel">Financer Screenshots</h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div className="modal-body p-0">
                <div id="financerCarousel" className="carousel slide" data-bs-ride="carousel">
                  <div className="carousel-indicators">
                    <button type="button" data-bs-target="#financerCarousel" data-bs-slide-to="0" className="active" aria-current="true" aria-label="Dashboard"></button>
                    <button type="button" data-bs-target="#financerCarousel" data-bs-slide-to="1" aria-label="Investments"></button>
                    <button type="button" data-bs-target="#financerCarousel" data-bs-slide-to="2" aria-label="Chit Management"></button>
                  </div>
                  <div className="carousel-inner">
                    <div className="carousel-item active">
                      <img src={financerDashboard} className="d-block w-100" alt="Financer Dashboard" />
                      <div className="carousel-caption d-none d-md-block bg-dark bg-opacity-50 rounded">
                        <h5>Dashboard</h5>
                        <p>Overview of your financial status and accounts</p>
                      </div>
                    </div>
                    <div className="carousel-item">
                      <img src={financerInvestments} className="d-block w-100" alt="Investments Management" />
                      <div className="carousel-caption d-none d-md-block bg-dark bg-opacity-50 rounded">
                        <h5>Investments</h5>
                        <p>Track and manage your investment portfolio</p>
                      </div>
                    </div>
                    <div className="carousel-item">
                      <img src={financerChitManagement} className="d-block w-100" alt="Chit Fund Management" />
                      <div className="carousel-caption d-none d-md-block bg-dark bg-opacity-50 rounded">
                        <h5>Chit Management</h5>
                        <p>Manage and track your chit funds efficiently</p>
                      </div>
                    </div>
                  </div>
                  <button className="carousel-control-prev" type="button" data-bs-target="#financerCarousel" data-bs-slide="prev">
                    <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                    <span className="visually-hidden">Previous</span>
                  </button>
                  <button className="carousel-control-next" type="button" data-bs-target="#financerCarousel" data-bs-slide="next">
                    <span className="carousel-control-next-icon" aria-hidden="true"></span>
                    <span className="visually-hidden">Next</span>
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
}
